import React from "react";

const DETAILS = [
  {
    id: "fleet-repair",
    number: "01",
    title: "Fleet Repair & Maintenance",
    summary:
      "In-shop and on-site repair, preventive maintenance programs, DOT inspections, brake, tire, and hydraulic work, plus emergency roadside support — everything needed to keep trucks and heavy equipment earning revenue instead of sitting idle.",
    projects: [
      {
        label: "Current Project · 2026",
        body:
          "Actively supporting waste-hauling companies in Lakeland, Florida with a 2026 fleet-readiness program — scheduled PMs, rapid turnaround repairs, and inspection support across active hauling routes.",
      },
      {
        label: "Project · Lakeland, FL · January 2026",
        body:
          "Contracted by a waste hauler for structural repair on the body of a front-load garbage truck — cutting, patching, and welding worn body sections back to service-ready condition in Lakeland, Florida.",
      },
    ],
  },
  {
    id: "excavation",
    number: "02",
    title: "Excavation & Grading",
    summary:
      "Site prep, cut-and-fill, trenching, drainage work, and finish grading for commercial, municipal, and utility projects. We move dirt on schedule and finish to spec so the next trade can start without delay.",
    projects: [
      {
        label: "Latest Project · 2026",
        body:
          "Aided in water-main excavation and final site grading on a completed utility project in Marathon, Florida.",
        image: {
          src: "/assets/projects/watermain-marathon.jpg",
          alt: "Water-main trench with newly laid blue pipes and finished grading — Marathon, Florida",
        },
      },
      {
        label: "Project · Marathon, FL · 2025",
        body:
          "Aided in storm-water installation in Marathon, Florida — setting large-diameter culvert pipe and forming the drainage corridor as part of a completed 2025 storm-water build-out.",
        image: {
          src: "/assets/projects/stormwater-marathon.jpg",
          alt: "Excavator lifting a large concrete storm-water pipe into a deep dirt pit — Marathon, Florida",
        },
      },
      {
        label: "Project · Fort Myers, FL · December 2025",
        body:
          "Aided in the excavation for a new sewer-main installation in Fort Myers, Florida — trenching alongside an active residential build so the utility could tie in without delay.",
      },
    ],
  },
  {
    id: "concrete",
    number: "03",
    title: "Concrete Services",
    summary:
      "Pours, patches, curbs, and load-bearing slabs — plus structural sealing engineered for high-water and storm-exposed structures where standard finishes fail.",
    projects: [
      {
        label: "Latest Project · North Port, FL · 2025",
        body:
          "Aided in mortar-sealing newly installed storm structures in North Port, Florida — sealing joints and interfaces on precast concrete inlets to lock in water integrity from day one.",
        image: {
          src: "/assets/projects/concrete-northport.jpg",
          alt: "Newly installed precast concrete storm-water inlets sitting on prepped ground — North Port, Florida",
        },
      },
    ],
  },
  {
    id: "pressure-washing",
    number: "04",
    title: "Commercial Pressure Washing",
    summary:
      "Fleet washing, service-bay degreasing, sidewalk and parking-lot restoration, and exterior building cleaning for commercial properties and industrial sites.",
    projects: [],
  },
  {
    id: "waste-recycling",
    number: "05",
    title: "Waste & Recycling Services",
    summary:
      "Container placement, scheduled hauling, single-stream and cardboard recycling programs, and route management — built around the reality of commercial and industrial operations.",
    projects: [],
  },
  {
    id: "porta-potty",
    number: "06",
    title: "Porta-Potty Services",
    summary:
      "Portable sanitation rentals for job sites, construction projects, and events — delivery, scheduled servicing, waste removal, and swap-outs handled end-to-end.",
    projects: [],
  },
];

export const ServiceDetails = () => {
  return (
    <section
      data-testid="service-details-section"
      id="service-details"
      className="relative py-24 sm:py-28 lg:py-32 px-6 sm:px-10"
      style={{ background: "var(--kz-bg-secondary)" }}
    >
      <div className="max-w-6xl mx-auto">
        {/* Section header */}
        <div className="text-center mb-16 sm:mb-20">
          <span
            className="font-display text-xs uppercase tracking-[0.4em] inline-block mb-4"
            style={{ color: "var(--kz-accent)" }}
          >
            In Depth
          </span>
          <h2
            data-testid="service-details-heading"
            className="font-display font-semibold text-3xl sm:text-4xl lg:text-5xl leading-[1.1] max-w-3xl mx-auto"
            style={{ color: "var(--kz-text-primary)" }}
          >
            What each service{" "}
            <span style={{ color: "var(--kz-accent)" }}>actually looks like.</span>
          </h2>
        </div>

        {/* Detail rows */}
        <div className="flex flex-col">
          {DETAILS.map((d, i) => (
            <article
              key={d.id}
              data-testid={`service-detail-${d.id}`}
              className={`service-detail grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-10 py-10 sm:py-12 ${
                i !== DETAILS.length - 1 ? "border-b" : ""
              }`}
              style={{ borderColor: "var(--kz-border)" }}
            >
              {/* Left: number + title */}
              <div className="md:col-span-4">
                <div
                  className="font-display text-xs uppercase tracking-[0.4em] mb-3"
                  style={{ color: "var(--kz-accent)" }}
                >
                  [ {d.number} ]
                </div>
                <h3
                  className="font-display font-semibold text-2xl sm:text-3xl leading-tight"
                  style={{ color: "var(--kz-text-primary)" }}
                >
                  {d.title}
                </h3>
              </div>

              {/* Right: summary + project */}
              <div className="md:col-span-8">
                <p
                  className="text-base sm:text-lg leading-relaxed"
                  style={{ color: "var(--kz-text-secondary)" }}
                >
                  {d.summary}
                </p>

                {d.projects && d.projects.length > 0 && (
                  <div className="mt-6 space-y-5">
                    {d.projects.map((p, pi) => (
                      <aside
                        key={pi}
                        data-testid={`service-project-${d.id}-${pi}`}
                        className={`project-card ${
                          p.image
                            ? "project-card--with-image"
                            : "p-5 sm:p-6 flex flex-col sm:flex-row gap-4 sm:gap-6 items-start"
                        }`}
                      >
                        {p.image && (
                          <div className="project-image-wrap">
                            <img
                              data-testid={`service-project-image-${d.id}-${pi}`}
                              src={p.image.src}
                              alt={p.image.alt}
                              className="project-image"
                              loading="lazy"
                            />
                          </div>
                        )}
                        <div
                          className={
                            p.image
                              ? "project-body p-5 sm:p-6 flex flex-col gap-3"
                              : "flex flex-col sm:flex-row gap-4 sm:gap-6 items-start w-full"
                          }
                        >
                          <div className="project-badge shrink-0 self-start">
                            <span className="project-dot" aria-hidden="true" />
                            <span className="font-display text-[10px] sm:text-[11px] uppercase tracking-[0.28em] font-semibold">
                              {p.label}
                            </span>
                          </div>
                          <p
                            className="text-sm sm:text-base leading-relaxed"
                            style={{ color: "var(--kz-text-primary)" }}
                          >
                            {p.body}
                          </p>
                        </div>
                      </aside>
                    ))}
                  </div>
                )}
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ServiceDetails;
